import { useEffect, useRef } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

const DEFAULT_DEBOUNCE_MS = 300;

const stripExcludedFields = <T extends Record<string, unknown>>(
  data: T,
  excludeFields: string[],
): T => {
  if (!excludeFields.length) return data;

  const next = { ...data };
  excludeFields.forEach((fieldName) => {
    delete next[fieldName];
  });
  return next;
};

/** Keeps in-memory-only fields (e.g. Aadhaar) when applying an external snapshot reset. */
const mergeSnapshotResetValues = <T extends FieldValues>(
  defaultValues: T,
  snapshot: T,
  currentValues: T,
  excludeFields: string[],
): T => {
  const merged = { ...defaultValues, ...snapshot } as T;

  if (!excludeFields.length) return merged;

  const current = currentValues as Record<string, unknown>;
  const target = merged as Record<string, unknown>;
  excludeFields.forEach((fieldName) => {
    if (current[fieldName] !== undefined) {
      target[fieldName] = current[fieldName];
    }
  });

  return merged;
};

export interface UseFormPersistOptions<T extends FieldValues> {
  defaultValues: T;
  /** Persisted slice; when it changes, the form is reset (merged over `defaultValues`). */
  snapshot?: T | null;
  onPersist: (data: T) => void;
  /** Field names omitted from the value passed to `onPersist` (e.g. sensitive IDs). */
  excludeFields?: string[];
  debounceMs?: number;
}

/**
 * Syncs react-hook-form state with an external persistence layer via `snapshot` + `onPersist`.
 * The hook does not read or write storage directly — wire sessionStorage, Zustand, etc. in callers.
 */
export const useFormPersist = <T extends FieldValues>(
  form: UseFormReturn<T>,
  {
    defaultValues,
    snapshot,
    onPersist,
    excludeFields = [],
    debounceMs = DEFAULT_DEBOUNCE_MS,
  }: UseFormPersistOptions<T>,
): void => {
  const lastAppliedSnapshotRef = useRef<string | null>(null);
  const onPersistRef = useRef(onPersist);
  onPersistRef.current = onPersist;

  const getPersistableData = (data: T) =>
    stripExcludedFields(data as Record<string, unknown>, excludeFields) as T;

  useEffect(() => {
    if (!snapshot) return;

    const serialized = JSON.stringify(snapshot);
    if (lastAppliedSnapshotRef.current === serialized) return;
    lastAppliedSnapshotRef.current = serialized;

    form.reset(
      mergeSnapshotResetValues(
        defaultValues,
        snapshot,
        form.getValues(),
        excludeFields,
      ),
    );
  }, [snapshot, form, defaultValues, excludeFields]);

  const persistSnapshot = useDebouncedCallback((data: T) => {
    const persistableData = getPersistableData(data);
    
    // Set the ref to match the serialized version of what we are about to persist.
    // This ensures that when the parent state updates and passes the snapshot back down,
    // the useEffect above will see that they match and skip the redundant form.reset().
    lastAppliedSnapshotRef.current = JSON.stringify(persistableData);
    
    onPersistRef.current(persistableData);
  }, debounceMs);

  useEffect(() => {
    const subscription = form.watch((data) => {
      persistSnapshot(data as T);
    });

    return () => {
      subscription?.unsubscribe?.();
      persistSnapshot.cancel();
    };
  }, [form, persistSnapshot]);
};
